import { useState } from "react";
import { toast } from "sonner";
import { Radio, RadioTower, AlertTriangle, Loader2, CheckCircle2, CircleSlash } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/**
 * CRSStatusBadge — surfaces the outbound "Booking CRS" state on a reservation.
 *
 * States shown to the host:
 *   - Not eligible : OTA-origin booking (no push needed) OR no OTA mappings yet
 *   - Not pushed   : Eligible but never pushed → offers a manual "Push to OTAs"
 *   - Pushed       : Live on Channex (green) → shows revoke option
 *   - Failed       : Last attempt errored (amber) → retry button
 *   - Revoked      : Cancel already pushed to OTAs (grey)
 *
 * Only rendered when booking is Direct / Walk-in / Atiti-origin. OTA-origin
 * bookings (Booking.com, Airbnb, MMT, Agoda, …) already exist on Channex.
 */
const _PMS_ORIGIN = ["direct", "walk-in", "walkin", "phone", "email", "atiti", "atiti homestay"];

export default function CRSStatusBadge({ booking, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!booking) return null;

  const channel = (booking.channel || "").trim().toLowerCase();
  const isPmsOrigin = _PMS_ORIGIN.includes(channel);
  if (!isPmsOrigin) return null; // OTA bookings — already on Channex via inbound webhook

  const crs = booking.crs_push || {};
  const pushed = !!booking.provider_booking_id && crs.ok && !crs.revoked_at;
  const revoked = !!crs.revoked_at;
  const failed = crs.ok === false && !revoked;
  const notPushed = !booking.provider_booking_id && !failed;

  const doPush = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/bookings/${booking.id}/push-to-crs`);
      if (data?.skipped) toast.info(`CRS: ${data.skipped.replaceAll("_", " ")}`);
      else toast.success("Booking pushed — OTAs will block these dates within 60s");
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const doRevoke = async () => {
    if (!confirm("Revoke this booking on connected OTAs? Their calendars will re-open the dates.")) return;
    setBusy(true);
    try {
      await api.post(`/pms/bookings/${booking.id}/revoke-from-crs`);
      toast.success("Revoked — OTAs will re-open the dates within 60s");
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (pushed) {
    return (
      <button data-testid="crs-badge-pushed" onClick={doRevoke} disabled={busy}
        title={`On OTAs since ${crs.pushed_at?.slice(0, 10) || ""} · click to revoke`}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={11} /> : <RadioTower size={11} />}
        On OTAs
      </button>
    );
  }
  if (revoked) {
    return (
      <span data-testid="crs-badge-revoked"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-slate-300 bg-slate-50 text-slate-500">
        <CircleSlash size={11} /> Revoked on OTAs
      </span>
    );
  }
  if (failed) {
    return (
      <button data-testid="crs-badge-failed" onClick={doPush} disabled={busy}
        title={crs.reason ? `Last error: ${crs.reason}` : "Retry push"}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={11} /> : <AlertTriangle size={11} />}
        Retry OTA push
      </button>
    );
  }
  if (notPushed) {
    return (
      <button data-testid="crs-badge-push" onClick={doPush} disabled={busy}
        title="Push this reservation to Channex so mapped OTAs mark the dates SOLD"
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60">
        {busy ? <Loader2 className="animate-spin" size={11} /> : <Radio size={11} />}
        Push to OTAs
      </button>
    );
  }
  return null;
}
